import { Badge, Button, Card, FieldGroupIconButton, Flex, Heading, Icon, Image, Label, Text, TextField, useTheme, View } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";

import { AuthSession, AuthUser } from "aws-amplify/auth";
import { uploadData } from 'aws-amplify/storage';
import { LatLng } from "leaflet";
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { GrUpload } from "react-icons/gr";
import { MdCameraAlt, MdClose } from "react-icons/md";
import PlaceDataUtils, { NewPlaceDefinition } from "../utils/PlaceDataUtils";
import StorageUtils from '../utils/StorageUtils';
import MapDetailPlaceSelection from "./MapDetailPlaceSelection";
import ImageUtils from '../utils/image/ImageUtils';
import ClientDeviceUtils from '../utils/client/ClientDeviceUtils';
import { generateUUID } from '../utils/UuidUtils';
import InputSanitizationUtils from '../utils/InputSanitizationUtils';

import './CreateNewPlacePopup.css';
import './PlaceOverviewPopup.css';

// Define interfaces for file processing
interface FileProcessingParams {
    file: File;
    key: string;
    metadata?: Record<string, string>;
}

// Interface for uploaded photos
interface UploadedPhoto {
    id: string;
    file: File;
    key: string;
    url: string;
    status: 'uploading' | 'uploaded' | 'error';
    isMainImage: boolean;
    isDuplicate: boolean;
}

// See interface definiton in UserInfoPopup
export default function CreateNewPlacePopup({ user, initialPlaceLatLen, handleCloseOverlay, handleNewPlaceCreation, handleShowLogin, /*userAttributes,*/ authSession }:
    {
        user: AuthUser,
        initialPlaceLatLen: LatLng,
        handleCloseOverlay: () => void,
        handleNewPlaceCreation: (newPlace: NewPlaceDefinition) => void,
        handleShowLogin: () => void,
        authSession: AuthSession | undefined
    }) {

    const PLACE_ADDRESS_FORM_ID: string = "placeGeneratedAddress"
    const PLACE_NAME_FORM_ID: string = "placeName"

    const MAX_PLACE_IMAGES: number = 6

    // TODO should addrString be stored, or dynamically generated on view (for browser language) - primarily probalby stored, to avoid to many geosearch requests
    const [generatedAddressString, setGeneratedAddressString] = useState("")
    const [userEnteredAddress, setUserEnteredAddress] = useState<string>("")
    const [userEditedAddress, setUserEditedAddress] = useState<boolean>(false)
    const [updatedPlaceLatLngString, setUpdatedPlaceLatLngString] = useState(initialPlaceLatLen.toString())
    const [updatedPlace, setUpdatedPlace] = useState<LatLng>(initialPlaceLatLen)

    const { t } = useTranslation();

    const { tokens } = useTheme();

    // Handle address generation from map - only update placeholder if user hasn't edited
    const handleAddressStringUpdate = (address: string): void => {
        setGeneratedAddressString(address);
        // Only update placeholder if user hasn't manually edited the address
        if (!userEditedAddress) {
            // If user hasn't entered anything and we get a new generated address, clear their entry
            if (!userEnteredAddress || userEnteredAddress === "") {
                setUserEnteredAddress("");
            }
        }
    };

    // Handle manual address input change
    const handleAddressInputChange = (value: string): void => {
        setUserEnteredAddress(value);
        if (value.trim() === "") {
            // If user deletes all text, revert to not edited state
            setUserEditedAddress(false);
        } else {
            // User has entered something, mark as edited
            setUserEditedAddress(true);
        }
    };

    // Handle clear address button click - resets to placeholder mode
    const handleClearAddress = (): void => {
        setUserEnteredAddress("");
        setUserEditedAddress(false);
    };

    function setNewPlace(newPlace: React.SetStateAction<LatLng>): void {
        newPlace && setUpdatedPlace(newPlace)
        newPlace && setUpdatedPlaceLatLngString(newPlace.toString())
    }

    const [selectedCategory, setSelectedCategory] = useState<string>("")
    const [selectedProperties, setSelectedProperties] = useState<string[]>([])

    const [placeCategoryEmpty, setPlaceCategoryEmpty] = useState<boolean>(false)

    const [hasDuplicateImages, setHasDuplicateImages] = useState<boolean>(false)

    const [newPlaceUuid, setNewPlaceUuid] = useState<string>()

    const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhoto[]>([])

    const [description, setDescription] = useState<string>("");

    const [isToolbarVisible, setIsToolbarVisible] = useState<boolean>(true);

    const [hasCameraSupport, setHasCameraSupport] = useState<boolean>(false);

    const cameraInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const toggleToolbarVisibility = (): void => {
        setIsToolbarVisible(!isToolbarVisible);
    };

    useEffect(() => {

        const uuid = generateUUID()
        setNewPlaceUuid(uuid)
        console.debug("New placeId just set: ", uuid);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleCloseOverlay();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        // Check for camera support - only show on mobile devices (phones/tablets)
        const checkCamera = () => {
            const isMobile = ClientDeviceUtils.isMobileDevice();
            setHasCameraSupport(isMobile);
        };
        checkCamera();

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [])

    // Separate useEffect for cleaning up photo URLs when component unmounts
    useEffect(() => {
        return () => {
            // Clean up photo URLs only on component unmount
            uploadedPhotos.forEach(photo => URL.revokeObjectURL(photo.url));
        };
    }, []) // Empty dependency array ensures this only runs on mount/unmount

    const submitForm = (formEvent: SyntheticEvent): void => {
        formEvent.preventDefault()
        const form = formEvent.currentTarget

        // Sanitize all user inputs before creating place
        const rawName = (form as HTMLFormElement)[PLACE_NAME_FORM_ID].value.toString();
        // Use user-entered address if exists and not empty, otherwise use generated address (which may be 'N/A')
        const rawAddress = userEnteredAddress.trim() !== "" ? userEnteredAddress : generatedAddressString;
        const rawDescription = description;

        const newPlaceHolder: NewPlaceDefinition = {
            id: newPlaceUuid ? newPlaceUuid : "",
            lat: updatedPlace.lat,
            lng: updatedPlace.lng,
            addressString: InputSanitizationUtils.sanitizePlainText(rawAddress),
            name: InputSanitizationUtils.sanitizePlainText(rawName),
            description: InputSanitizationUtils.sanitizeRichText(rawDescription),
            placeCategories: [],
            placeProperties: selectedProperties,
            creatorUserId: user.userId
        }

        if (selectedCategory) {
            setPlaceCategoryEmpty(false)
            newPlaceHolder.placeCategories = [selectedCategory]
        } else {
            setPlaceCategoryEmpty(true)
            return
        }

        // Check for duplicate images before submission
        if (hasDuplicateImages) {
            return // Prevent submission if duplicates exist
        }

        handleNewPlaceCreation(newPlaceHolder)
    }

    const handlePlaceCreationCanceled = async (): Promise<void> => {
        // Delete all uploaded place images files
        try {
            await Promise.all(
                uploadedPhotos.map(photo => StorageUtils.deleteFile(photo.key))
            );
        } catch (error) {
            console.error("Error deleting place images: ", error);
        }
        handleCloseOverlay()
    }

    const handlePlaceCategoryClicked = (event: React.MouseEvent<HTMLElement>): void => {

        const category = event.currentTarget.id
        setPlaceCategoryEmpty(false)
        setSelectedCategory(category)
    }

    const handlePlacePropertyClicked = (event: React.MouseEvent<HTMLElement>): void => {
        const clickedProperty: string = event.currentTarget.id
        console.info("Place property cliecked event: ", clickedProperty)
        console.info("Place property list: ", selectedProperties)

        // Either remove or add clicked property from list
        if (selectedProperties.includes(clickedProperty)) {
            // Remove
            const reducedPropertiesList = selectedProperties.filter(p => p != clickedProperty)
            setSelectedProperties(
                reducedPropertiesList
            )
        } else {
            setSelectedProperties([...selectedProperties, clickedProperty])
        }

    }

    const switchToLogin = (): void => {
        handleCloseOverlay()
        handleShowLogin()
    }

    // If user is not logged in - show login button and disable all fields.
    const userIn: boolean = user != undefined;

    const getFileExtension = (filename: string): string => {
        return filename.split('.').pop() || ''
    };

    // Function to re-evaluate all duplicates in the current photo list
    const reEvaluateDuplicates = async (photos: UploadedPhoto[]): Promise<UploadedPhoto[]> => {
        console.debug("reEvaluateDuplicates called with", photos.length, "photos");
        const updatedPhotos = [...photos];
        
        // Reset all duplicate flags first
        for (let i = 0; i < updatedPhotos.length; i++) {
            updatedPhotos[i] = { ...updatedPhotos[i], isDuplicate: false };
        }
        
        // Create groups of identical images
        const duplicateGroups: number[][] = [];
        
        for (let i = 0; i < updatedPhotos.length; i++) {
            const currentPhoto = updatedPhotos[i];
            if (currentPhoto.status !== 'uploaded') continue; // Skip non-uploaded photos
            
            // Check if this photo is already in a group
            let foundGroup = false;
            for (const group of duplicateGroups) {
                if (group.includes(i)) {
                    foundGroup = true;
                    break;
                }
            }
            
            if (foundGroup) continue; // Skip if already processed
            
            // Find all photos that match this one
            const matchingIndices = [i];
            
            for (let j = i + 1; j < updatedPhotos.length; j++) {
                const otherPhoto = updatedPhotos[j];
                if (otherPhoto.status !== 'uploaded') continue; // Skip non-uploaded photos
                
                try {
                    const currentPhotoBase64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.readAsDataURL(currentPhoto.file);
                    });
                    
                    const otherPhotoBase64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.readAsDataURL(otherPhoto.file);
                    });
                    
                    if (currentPhotoBase64 === otherPhotoBase64) {
                        matchingIndices.push(j);
                    }
                } catch (error) {
                    console.error("Error comparing photos: ", error);
                }
            }
            
            // If we found duplicates (more than 1 matching image), add to groups
            if (matchingIndices.length > 1) {
                duplicateGroups.push(matchingIndices);
            }
        }
        
        // Mark all images in duplicate groups as duplicates
        for (const group of duplicateGroups) {
            console.debug("Found duplicate group:", group);
            for (const index of group) {
                updatedPhotos[index] = { ...updatedPhotos[index], isDuplicate: true };
            }
        }
        
        // Update the duplicate state
        const hasDuplicates = updatedPhotos.some(photo => photo.isDuplicate);
        console.debug("Duplicate evaluation complete. Has duplicates:", hasDuplicates);
        setHasDuplicateImages(hasDuplicates);
        
        return updatedPhotos;
    };

    const renameFile = (params: FileProcessingParams, isMainImage: boolean): FileProcessingParams => {
        const extension = getFileExtension(params.key);
        const newUuid = generateUUID();
        const prefix = isMainImage ? StorageUtils.PLACE_PHOTOS_MAIN_IMAGE_PREFIX : '';
        return {
            ...params,
            key: `${prefix}${newUuid}.${extension}`
        };
    };

    const addFileMetadata = ({ file, key }: FileProcessingParams): FileProcessingParams => {
        return {
            file,
            key,
            metadata: {
                [StorageUtils.UPLOADER_USER_ID_METADATA_KEY]: user.userId,
                [StorageUtils.ORIGINAL_FILE_NAME_METADATA_KEY]: file.name
            },
        };
    };

    // Main file processing and upload method
    const uploadPhoto = async (file: File): Promise<void> => {
        const photoId = generateUUID();
        const isMainImage = !hasMainImage(); // Main image if no main image exists yet

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);

        // Add photo to state with "uploading" status
        const newPhoto: UploadedPhoto = {
            id: photoId,
            file: file,
            key: '', // Will be updated after processing
            url: previewUrl,
            status: 'uploading',
            isMainImage: isMainImage,
            isDuplicate: false
        };

        setUploadedPhotos(prev => [...prev, newPhoto]);

        try {
            // Process the file: rename and add metadata
            let params: FileProcessingParams = {
                file: file,
                key: file.name
            };

            params = renameFile(params, isMainImage);
            params = addFileMetadata(params);

            // Resize image to 1024x1024
            const resizedFileObject = await ImageUtils.resizeImage(params.file, ImageUtils.DEFAULT_PLACE_IMAGE_SIZE, ImageUtils.DEFAULT_PLACE_IMAGE_SIZE);
            if (resizedFileObject instanceof File) {
                params.file = resizedFileObject;
            }

            console.warn("Processed file params: ", params);

            // Upload to storage using Amplify uploadData
            const uploadPath = `${StorageUtils.PLACE_PHOTOS}/${newPlaceUuid}/${params.key}`;
            const uploadResult = await uploadData({
                path: uploadPath,
                data: params.file,
                options: {
                    contentType: params.file.type,
                    metadata: params.metadata
                }
            }).result;

            console.warn("Photo upload result: ", uploadResult);

            // Update photo status to uploaded and set the key
            setUploadedPhotos(prev => {
                const updatedPhotos = prev.map(photo =>
                    photo.id === photoId
                        ? { ...photo, key: uploadResult.path, status: 'uploaded' as const }
                        : photo
                );
                
                // Re-evaluate duplicates with the updated photo list
                reEvaluateDuplicates(updatedPhotos).then(photosWithDuplicates => {
                    setUploadedPhotos(photosWithDuplicates);
                });
                
                return updatedPhotos;
            });

        } catch (error) {
            console.error("Error uploading photo: ", error);
            // Update photo status to error
            setUploadedPhotos(prev => prev.map(photo =>
                photo.id === photoId
                    ? { ...photo, status: 'error' as const, isDuplicate: false }
                    : photo
            ));
        }
    };

    // Handler for camera photo capture
    const handleCameraCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];
        console.debug("Camera captured file: ", file);

        await uploadPhoto(file);

        // Reset the input so the same file can be selected again
        if (cameraInputRef.current) {
            cameraInputRef.current.value = '';
        }
    };

    // Handler for file selection from browser
    const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
        // Currently selector only allows to select one file at a time
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        // Upload all selected files
        for (let i = 0; i < files.length; i++) {
            if (uploadedPhotos.length + i < MAX_PLACE_IMAGES) {
                await uploadPhoto(files[i]);
            }
        }

        // Reset the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handler to trigger camera input
    const handleTakePhotoClick = () => {
        if (cameraInputRef.current) {
            cameraInputRef.current.click();
        }
    };

    // Handler to delete photo
    const handleDeletePhoto = async (photoId: string) => {
        const photoToDelete = uploadedPhotos.find(photo => photo.id === photoId);
        if (!photoToDelete) return;

        try {
            // Delete from storage if uploaded
            if (photoToDelete.status === 'uploaded' && photoToDelete.key) {
                await StorageUtils.deleteFile(photoToDelete.key);
            }

            // Revoke object URL
            URL.revokeObjectURL(photoToDelete.url);

            // Remove from state and get updated list
            const updatedPhotos = uploadedPhotos.filter(photo => photo.id !== photoId);

            // Re-evaluate duplicates after removal
            const photosWithUpdatedDuplicates = await reEvaluateDuplicates(updatedPhotos);
            setUploadedPhotos(photosWithUpdatedDuplicates);

        } catch (error) {
            console.error("Error deleting photo: ", error);
        }
    };

    // Handler to trigger file browser
    const handleBrowseFilesClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Helper function to check if a photo is the main image
    const isPhotoMain = (photo: UploadedPhoto): boolean => {
        return !!(photo.status === 'uploaded' && photo.key && photo.key.includes(StorageUtils.PLACE_PHOTOS_MAIN_IMAGE_PREFIX));
    };

    // Helper function to check if there is a main image uploaded
    const hasMainImage = (): boolean => {
        return uploadedPhotos.some(isPhotoMain);
    };

    // Custom upload UI component with integrated upload placeholder
    const getImageUploadUI = () => {
        const canUploadMore = uploadedPhotos.length < MAX_PLACE_IMAGES;

        return (
            <Flex direction="column" gap="medium" className="custom-image-uploader">
                {/* Hidden file inputs */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelection}
                    disabled={!userIn || !canUploadMore}
                />

                {/* Photo gallery with upload placeholder */}
                <Flex
                    className="place-image-upload-image-list-flex"
                    direction="row"
                    wrap="nowrap"
                    gap="small"
                >
                    {/* Existing uploaded photos */}
                    {uploadedPhotos.map((photo) => {
                        const isMain = isPhotoMain(photo);
                        
                        return (
                        <Flex
                            key={photo.id}
                            className={`place-image-upload-image-flex-create${isMain ? " main-image-create" : ""}${photo.status === 'error' ? " error-image-create" : ""}${photo.isDuplicate ? " duplicate-image-create" : ""}`}
                        >
                            <Image
                                className="place-image-upload-image-create"
                                src={photo.url}
                                alt={photo.file.name}
                            />
                            <Button
                                className="place-image-upload-image-flex-button-create"
                                variation="link"
                                size="small"
                                onClick={() => handleDeletePhoto(photo.id)}
                                isDisabled={photo.status === 'uploading'}
                            >
                                <Flex
                                    direction="column"
                                    alignItems="center"
                                    gap=".4rem"
                                >
                                    {isMain && (
                                        <Label>{t('place.creation.main-image')}</Label>
                                    )}
                                    {photo.status === 'uploading' && (
                                        <Label>{t('file-upload.uploading')}</Label>
                                    )}
                                    {photo.status === 'error' && (
                                        <Label color="red">{t('file-upload.error')}</Label>
                                    )}
                                    <Icon
                                        fontSize="large"
                                        color="font.error"
                                        viewBox={{ width: 512, height: 512 }}
                                        paths={[
                                            {
                                                d: 'M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z',
                                                strokeWidth: '32',
                                                fill: 'none',
                                                strokeMiterlimit: '10',
                                                stroke: 'currentColor',
                                            },
                                            {
                                                d: 'M320 320L192 192m0 128l128-128',
                                                strokeWidth: '32',
                                                fill: 'none',
                                                strokeLinecap: 'round',
                                                stroke: 'currentColor',
                                            },
                                        ]}
                                    />
                                </Flex>
                            </Button>
                        </Flex>
                        );
                    })}

                    {/* Upload placeholder - always visible if can upload more */}
                    {canUploadMore && (
                        <Flex
                            className="place-image-upload-placeholder"
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                            gap="xs"
                        >
                            {hasCameraSupport ? (
                                // Show both buttons on mobile devices
                                <>
                                    <Flex direction="column" alignItems="center" gap="xs">
                                        <Button
                                            variation="link"
                                            colorTheme="overlay"
                                            onClick={handleTakePhotoClick}
                                            isDisabled={!userIn}
                                            className="upload-placeholder-button-with-camera"
                                        >
                                            <MdCameraAlt
                                                size="2rem"
                                                className="upload-placeholder-button-single-icon-with-camera"
                                            />
                                        </Button>
                                        <Button
                                            variation="link"
                                            colorTheme="overlay"
                                            onClick={handleBrowseFilesClick}
                                            isDisabled={!userIn}
                                            className="upload-placeholder-button-with-camera"
                                        >
                                            <GrUpload
                                                size="2rem"
                                                className="upload-placeholder-button-single-icon-with-camera"
                                            />
                                        </Button>
                                        <Text >{t(!hasMainImage() ? "place.creation.main-image-upload" : "file-upload.browse-images")}</Text>
                                    </Flex>
                                </>
                            ) : (
                                // Show only browse button on desktop
                                <Button
                                    variation="link"
                                    colorTheme="overlay"
                                    // size="small"
                                    onClick={handleBrowseFilesClick}
                                    isDisabled={!userIn}
                                    className="upload-placeholder-button-single"
                                >
                                    <Flex direction="column" alignItems="center" gap="xs">
                                        <GrUpload
                                            size="2rem"
                                            className="upload-placeholder-button-single-icon"
                                        />
                                        <Text >{t(!hasMainImage() ? "place.creation.main-image-upload" : "file-upload.browse-images")}</Text>
                                    </Flex>
                                </Button>
                            )}
                        </Flex>
                    )}
                </Flex>
            </Flex>
        );
    }


    return (
        <>
            <View className="overlay-content"
                // TODO move style to .css
                backgroundColor={tokens.colors.background.secondary}
            >
                {/* Fixed Top Bar */}
                <View className="place-overview-top-bar">
                    <Flex 
                        className="place-overview-top-bar-content"
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={tokens.space.medium}
                        wrap="wrap"
                    >
                        <Flex className="place-overview-top-bar-left">
                            <Heading level={2} className="create-new-place-top-bar-heading">
                                {userIn ? t('place.creation.title') : t('place.creation.you-have-to-login')}
                            </Heading>
                            <TextField
                                label=""
                                labelHidden
                                value={`${updatedPlace.lat.toFixed(6)}, ${updatedPlace.lng.toFixed(6)}`}
                                isDisabled
                                size="small"
                            />
                        </Flex>
                        <Flex
                            className="place-overview-top-bar-right"
                            direction="row"
                            alignItems="center"
                            gap={tokens.space.small}
                        >
                            <Button
                                className="place-overview-close-button"
                                variation="link"
                                size="large"
                                onClick={handlePlaceCreationCanceled}
                            >
                                ✕
                            </Button>
                        </Flex>
                    </Flex>
                </View>

                <View className="overlay-content-scrollable"
                    padding={tokens.space.medium}
                >
                    <Card>
                    <Flex
                        direction="column"
                    >
                        {!userIn &&
                            <>
                                <Button
                                    variation="primary"
                                    colorTheme="success"
                                    size="large"
                                    loadingText=""
                                    type="submit"
                                    onClick={switchToLogin}
                                >
                                    {t('user.sign-in')}
                                </Button>
                                <Button
                                    variation="primary"
                                    colorTheme="warning"
                                    size="large"
                                    loadingText=""
                                    onClick={handlePlaceCreationCanceled}
                                >
                                    {t('place.creation.close')}
                                </Button>
                            </>
                        }
                    </Flex>
                    <form onSubmit={submitForm}>
                        <Flex
                            direction="column"
                            gap="small"
                            alignContent="flex-start"
                            className="overlay-content-header"
                            textAlign="left"
                        >
                            <Card variation="elevated" className="create-new-place-map-card">
                                <MapDetailPlaceSelection
                                    newPlacePosition={initialPlaceLatLen}
                                    setAddressString={handleAddressStringUpdate}
                                    setNewPlace={setNewPlace}
                                />
                            </Card>
                            <TextField
                                id={PLACE_ADDRESS_FORM_ID}
                                className="text-input place-address-input"
                                label={t('place.creation.generated-address-label')}
                                labelHidden
                                size="large"
                                isRequired={false}
                                isDisabled={!userIn}
                                placeholder={generatedAddressString || t('place.creation.n-a')}
                                value={userEnteredAddress}
                                onChange={(e) => handleAddressInputChange(e.target.value)}
                                innerEndComponent={
                                    userEditedAddress && userIn ? (
                                        <FieldGroupIconButton
                                            ariaLabel={t('place.creation.clear-address')}
                                            variation="link"
                                            onClick={handleClearAddress}
                                        >
                                            <MdClose />
                                        </FieldGroupIconButton>
                                    ) : undefined
                                }
                            />
                            <TextField
                                id={PLACE_NAME_FORM_ID}
                                className="text-input"
                                label={
                                    <Text
                                        fontWeight={tokens.fontWeights.bold}
                                    >
                                        {t('place.creation.new-place-name')}
                                    </Text>
                                }
                                size="large"
                                isRequired={true}
                                isDisabled={!userIn}
                            />
                            <div className={`description-editor-container ${!isToolbarVisible ? 'toolbar-hidden' : ''}`}>
                                <div className="editor-header">
                                    <Label className="editor-header-label">
                                        {t('place.creation.new-place-description')}
                                    </Label>
                                    <Button
                                        variation="link"
                                        size="small"
                                        onClick={toggleToolbarVisibility}
                                        className="toolbar-toggle-button"
                                    >
                                        {isToolbarVisible ? t('place.creation.hide-toolbar') : t('place.creation.show-toolbar')}
                                    </Button>
                                </div>
                                <ReactQuill
                                    className="description-editor text-input"
                                    value={description}
                                    onChange={setDescription}
                                    theme="snow"
                                    readOnly={!userIn}
                                    placeholder={t('place.creation.description-placeholder')}
                                    formats={[
                                        'bold', 'italic', 'underline',
                                        'strike', 'blockquote',
                                        'list', 'bullet',
                                        'link', 'image'
                                    ]}
                                    modules={{
                                        toolbar: [
                                            ['bold', 'italic', 'underline'],
                                            ['strike', 'blockquote'],
                                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                            ['link', 'image']
                                        ],
                                        clipboard: {
                                            matchVisual: false
                                        }
                                    }}
                                />
                            </div>

                            {/* TODOs for place picute upload
                            - add thumbnails
                            - store under place id? Force place id??
                            */}
                            <Label
                                fontWeight={tokens.fontWeights.bold}
                                fontSize={tokens.fontSizes.large}
                            >
                                {t('place.creation.images')} {uploadedPhotos.length}/{MAX_PLACE_IMAGES}:
                            </Label>
                            
                            {/* Duplicate warning message */}
                            {uploadedPhotos.some(photo => photo.isDuplicate) && (
                                <Label
                                    color="font.error"
                                    fontWeight={tokens.fontWeights.bold}
                                    fontSize={tokens.fontSizes.medium}
                                >
                                    {t('place.creation.duplicate')}
                                </Label>
                            )}

                            {/* Hidden camera input */}
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                style={{ display: 'none' }}
                                onChange={handleCameraCapture}
                                disabled={!userIn}
                            />

                            {/* Unified image upload UI with integrated buttons */}
                            <Card
                                variation="elevated"
                                // className="image-upload-card"
                                className="content-field"
                            >
                                {getImageUploadUI()}
                            </Card>

                            <Label>New place id: {newPlaceUuid}</Label>

                            <Label
                                fontWeight={tokens.fontWeights.bold}
                                fontSize={tokens.fontSizes.large}
                            >
                                {t('place.category')}:
                            </Label>
                            <Card
                                variation="elevated"
                                // className={"content-field" + placeCategoryEmpty && "content-field-empty"}
                                className={"content-field" + (placeCategoryEmpty == true ? " content-field-empty" : "")}
                            >
                                <Flex
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignContent="flex-start"
                                    wrap="wrap"
                                >
                                    {PlaceDataUtils.getPlaceCategories().map((categoryEnum: string) => {
                                        return (
                                            <Badge
                                                key={categoryEnum}
                                                id={categoryEnum}
                                                size="large"
                                                className={selectedCategory == categoryEnum ?
                                                    "noselect place-category-badge place-category-badge-pressed" :
                                                    "noselect place-category-badge place-category-badge"
                                                }
                                                onClick={handlePlaceCategoryClicked}

                                            >
                                                <Flex>
                                                    <Image
                                                        alt=""
                                                        objectFit="none"
                                                        src={PlaceDataUtils.getPlaceCategoryDetail(categoryEnum)?.iconPath}
                                                    />
                                                    {/* Alternatively ToogleButton */}
                                                    <Label
                                                        whiteSpace="nowrap"
                                                    >
                                                        {t('place.categories.' + categoryEnum)}
                                                    </Label>
                                                </Flex>
                                            </Badge>
                                        )
                                    })}
                                </Flex>
                            </Card>
                            {/* Place properties: */}
                            <Label
                                fontWeight={tokens.fontWeights.bold}
                                fontSize={tokens.fontSizes.large}
                            >
                                {t('place.property')}:
                            </Label>
                            <Card
                                variation="elevated"
                                className="content-field"
                            >
                                <Flex
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignContent="flex-start"
                                    wrap="wrap"
                                >
                                    {PlaceDataUtils.getPlaceProperties().map((propertyEnum: string) => {
                                        return (
                                            <Badge
                                                key={propertyEnum}
                                                id={propertyEnum}
                                                size="large"
                                                className={selectedProperties.includes(propertyEnum) ?
                                                    "noselect place-category-badge place-property-badge-pressed" :
                                                    "noselect place-category-badge place-property-badge"
                                                }
                                                onClick={handlePlacePropertyClicked}

                                            >
                                                <Flex>
                                                    {/* Alternatively ToogleButton */}
                                                    <Label
                                                        whiteSpace="nowrap"
                                                    >
                                                        {t('place.properties.' + propertyEnum)}
                                                    </Label>
                                                </Flex>
                                            </Badge>
                                        )
                                    })}
                                </Flex>
                            </Card>
                            <Flex
                                direction="row"
                                justifyContent="space-around"
                            >
                                <Button
                                    variation="primary"
                                    colorTheme="warning"
                                    size="large"
                                    loadingText=""
                                    onClick={handleCloseOverlay}
                                >
                                    {t('place.creation.close')}
                                </Button>
                                <Button
                                    variation="primary"
                                    colorTheme="success"
                                    size="large"
                                    loadingText=""
                                    type="submit"
                                    isDisabled={!userIn || hasDuplicateImages}
                                >
                                    {t('place.creation.save')}
                                </Button>
                            </Flex>
                        </Flex>
                    </form>
                </Card>
                </View>
            </View>
        </>
    );
}